import { createSelector } from 'reselect';

import {
  DOCUMENT_INTAKE_SEGMENT_STATUS_FILED,
  DOCUMENT_INTAKE_STATUS_LABEL,
  DocumentIntakeJobStatus,
} from 'constants/documentIntake';

const selectSlice = state => state.documentIntake;

export const selectActiveJobIdForLoan = loanApplicationId =>
  createSelector(selectSlice, slice => slice.activeJobIdByLoan[loanApplicationId] ?? null);

export const selectActiveJobForLoan = loanApplicationId =>
  createSelector(selectSlice, slice => {
    const id = slice.activeJobIdByLoan[loanApplicationId];

    if (!id) {
      return null;
    }

    return slice.jobsById[id] ?? null;
  });

export const selectIsCancellingJob = id =>
  createSelector(selectSlice, slice => !!slice.cancellingJobIds[id]);

/**
 * Derive display-friendly status vocabulary from a job.
 * Maps backend status + progress counters → a label the UI renders.
 *
 * DEV-4526: includes a source label when present so the card shows
 * "Processing <label> — N of M" for in-progress multi-file jobs.
 *
 * DEV-4552: prefers the backend-provided `sourceLabel` DTO field (persists
 * across hard-reload) and falls back to the FE-only `uploadLabel` (same-session).
 *
 * @param {{
 *   status: string,
 *   processedSegments: number|null,
 *   totalSegments: number|null,
 *   sourceLabel?: string|null,
 *   uploadLabel?: string|null,
 * }} job
 * @returns {string}
 */
export const deriveIntakeDisplayStatus = job => {
  if (!job) {
    return '';
  }

  const { status, processedSegments, totalSegments, sourceLabel, uploadLabel } = job;

  if (status === DocumentIntakeJobStatus.IN_PROGRESS && totalSegments > 0) {
    const label = sourceLabel ?? uploadLabel ?? null;
    const labelPart = label ? ` ${label}` : '';

    return `Processing${labelPart} — ${processedSegments ?? 0} of ${totalSegments}`;
  }

  return DOCUMENT_INTAKE_STATUS_LABEL[status] ?? status;
};

/**
 * Return true when a Completed job still has at least one segment that has NOT been
 * filed yet (i.e. its status in resultJson is anything other than "Filed").
 *
 * Used by initIntakeForLoanAction to decide whether to resurface a Completed job
 * after a hard reload. A job whose every segment is Filed is considered "done" and
 * should NOT be re-surfaced — the user dismissed it by filing everything.
 *
 * A Completed job with no segments at all (resultJson missing / empty segments array)
 * is treated as fully-filed to avoid resurfacing an empty review drawer.
 *
 * @param {object|null} job - a normalised intake job from the store
 * @returns {boolean}
 */
export const hasUnfiledSegments = job => {
  const segments = job?.resultJson?.segments;

  if (!Array.isArray(segments) || segments.length === 0) {
    return false;
  }

  return segments.some(seg => seg.status !== DOCUMENT_INTAKE_SEGMENT_STATUS_FILED);
};
